import React, { useState, useEffect, useRef } from 'react';
import StatusBar from '../components/StatusBar.js';
import './LabelingPage.css';
import { useVerbatim } from '../context/VerbatimContext';
import { useSetup } from '../context/SetupContext';
import MinimalistButton from "../components/buttons/MinimalistButton";
import axios from 'axios';
import Spinner from '../components/Spinner.js';
import { checkSourceFromUrl } from '../utils/URLUtils.js'
import { extractDomainInfo } from '../utils/URLUtils';
import { imageToBase64, isBase64Image, formatVerbatimContent, migrateVerbatimFormat, base64ToImage } from '../utils/VerbatimUtils';

const TRANSLATION_ENDPOINT = process.env.NODE_ENV === 'development'
  ? 'https://vlabeler.dynvibe.com/translation/translate'
  : '/translation/translate';

const LabelingPage = () => {
  const { labels, labelTypes, addLabel, setLabels } = useSetup();
  const { verbatimToEdit, setVerbatimToEdit, getVerbatims, addVerbatim, editVerbatim, deleteVerbatim } = useVerbatim();
  const [verbatimContent, setVerbatimContent] = useState([{ type: 'text', content: '' }]);
  const [link, setLink] = useState('');
  const [isLockActive, setIsLockActive] = useState(false);
  const [activeLabels, setActiveLabels] = useState({});
  const [suggestedLabels, setSuggestedLabels] = useState([]);
  const [tabs, setTabs] = useState([]);
  const [activeTab, setActiveTab] = useState('');
  const [lastID, setLastID] = useState(0);
  const [editingVerbatimId, setEditingVerbatimId] = useState(null);
  const [editingVerbatimTranslation, setEditingVerbatimTranslation] = useState('');
  const [autoTranslationOnSave, setAutoTranslationOnSave] = useState(false);
  const imageInputRef = useRef(null);

  const [sourceOptions, setSourceOptions] = useState(labels['Sources']?.map(label => label.name) || ["Blog", "Forum", "Review"]);
  const genderOptions = ['Woman', 'Man', 'Unknown Gender'];
  const ageOptions = ['<25', '25-34', '35-44', '45-54', '55+', 'Unknown Age'];
  const sentiments = ['positive', 'negative', 'mixed', 'neutral'];
  const [statusMessage, setStatusMessage] = useState('');
  const [statusType, setStatusType] = useState('success');

  const [storedVerbatims, setStoredVerbatims] = useState([]);
  const [showTranscriptButton, setShowTranscriptButton] = useState(false); // Nouvel état pour vérifier si l'URL est TikTok
  const [loadTranscriptFromURlLoading, setLoadTranscriptFromURlLoading] = useState(false)
  const [transcriptError, setTranscriptError] = useState("")
  const selectedRef = useRef(null);

  const updateStatusMessage = (message, type = 'success') => {
    setStatusType(type);
    setStatusMessage(message);
  };

  useEffect(() => {
    const verbatims = getVerbatims();
    setStoredVerbatims(verbatims);
  }, [getVerbatims]);

  useEffect(() => {
    // Update the labeling tabs when either `labelTypes` or `activeTab` changes
    updateLabelingTabs();  // Call the function to update the tabs based on current state
  }, [labelTypes, activeTab]);  // Dependency array to ensure it runs when these variables change


  useEffect(() => {
    if (verbatimToEdit) {
      // Vérifier si le verbatim est dans l'ancien format et le migrer si nécessaire
      const migratedVerbatim = migrateVerbatimFormat(verbatimToEdit);
      const hasRenderableContent = Array.isArray(migratedVerbatim.verbatim)
        && migratedVerbatim.verbatim.some((item) => (
          item
          && typeof item === 'object'
          && typeof item.type === 'string'
          && ['text', 'image'].includes(item.type.trim().toLowerCase())
          && typeof item.content === 'string'
          && item.content.trim() !== ''
        ));
      
      // Définir le contenu du verbatim avec le nouveau format
      setVerbatimContent(
        migratedVerbatim.verbatim && (
          typeof migratedVerbatim.verbatim === 'string'
            ? migratedVerbatim.verbatim.trim() !== ''
            : hasRenderableContent
        )
          ? migratedVerbatim.verbatim
          : [{ type: 'text', content: '' }]
      );
      setLink(migratedVerbatim.link);
      setActiveLabels(migratedVerbatim.labels);
      setEditingVerbatimId(migratedVerbatim.id);
      setEditingVerbatimTranslation(migratedVerbatim.translation || '');
      setAutoTranslationOnSave(Boolean(migratedVerbatim.autoTranslationOnSave));

      setVerbatimToEdit(null);
    }
  }, [verbatimToEdit, setVerbatimToEdit]);

  useEffect(() => {
    const selectedSource = Array.isArray(activeLabels?.["Sources"])
      ? activeLabels["Sources"][0]?.name // si tu n’autorises qu’une sélection
      : null;

    if (selectedRef.current && selectedSource) {
      selectedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "nearest"
      });
    }
  }, [activeLabels]);

  useEffect(() => {
    // Update the labels displayed on the page based on current suggestions and active tab
    updateLabelingPageLabels();  // Call the function to refresh the labels on the page
  }, [suggestedLabels, activeTab, activeLabels]);  // Dependencies: suggested labels, active tab, and active labels

  useEffect(() => {
    // Refresh the list of selected labels whenever `activeLabels` changes
    updateSelectedLabelsList();  // Call the function to update the list of selected labels
  }, [activeLabels]);  // Dependency array with `activeLabels`

  useEffect(() => {
    // Automatically suggest labels based on the content of the verbatim text
    const allText = verbatimContent
      .filter(item => item.type === 'text')
      .map(item => item.content)
      .join(' ');
    suggestLabelsFromVerbatim(allText);
  }, [verbatimContent, labels]);  // Dependencies: `verbatim` content and available `labels`

  useEffect(() => {
    if (Array.isArray(storedVerbatims) && storedVerbatims.length > 0) {
      const maxID = Math.max(...storedVerbatims.map((v) => v.id));
      setLastID(maxID);
    } else {
      setLastID(0); // Start from 0 if no verbatims exist
    }
  }, [storedVerbatims]);


  /************************************************
   * STATE AND UI UPDATE FUNCTIONS 
   ************************************************/

  const generateNextID = () => {
    const maxID = Array.isArray(storedVerbatims) && storedVerbatims.length > 0
      ? Math.max(...storedVerbatims.map((v) => v.id))
      : 0;
    return maxID + 1;
  };


  // Function to update the available labeling tabs
  const updateLabelingTabs = () => {
    // Filter out specific labels ('Gender', 'Age', 'Sentiment') from the labelTypes and set the available tabs
    const availableTabs = Object.keys(labelTypes).filter(type => !['Gender', 'Age', 'Sentiment', 'Sources'].includes(type));

    setTabs(availableTabs);  // Update the state to reflect the available tabs

    // Set a default tab if none is active and there are available tabs
    if (availableTabs.length > 0 && !activeTab) {
      setActiveTab(availableTabs[0]);  // Set the first available tab as the default active tab
    }
  };

  // Function to update the list of selected labels displayed on the page
  const updateSelectedLabelsList = () => {
    const list = document.getElementById('selected-labels-list'); // Get the container for selected labels
    if (!list) return;
    list.innerHTML = ''; // Clear any existing content

    // Loop through active labels and render them in the UI
    Object.entries(activeLabels ?? {}).forEach(([labelType, labelsArray]) => {
      const labels = Array.isArray(labelsArray) ? labelsArray : labelsArray ? [labelsArray] : []; // Ensure labelsArray is an array

      // Iterate through each label object within the current label type
      labels.forEach((labelObj) => {
        if (labelObj && labelObj.name && labelObj.state) { // Ensure the label object has valid name and state
          const { name, state } = labelObj; // Destructure the name and state from the label object
          const div = document.createElement('div'); // Create a new div for displaying the label

          // Handle Gender label type with specific class based on name
          if (labelType === 'Gender') {
            div.textContent = `Gender: ${name}`; // Display "Gender: Man" or "Gender: Woman"
            div.className = `gender-label gender-${name.toLowerCase().replace(/\s+/g, '-')}`; // Apply specific CSS class
          }
          // Handle Age label type with specific class based on name
          else if (labelType === 'Age') {
            div.textContent = `Age: ${name}`; // Display "Age: 25-34"
            div.className = `age-label age-${name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`; // Apply specific CSS class
          }
          // Handle Sentiment label type with specific class based on state
          else if (labelType === 'Sentiment') {
            div.textContent = `Sentiment: ${state}`; // Display "Sentiment: Positive"
            div.className = `sentiment-label ${state.toLowerCase()}`; // Apply sentiment-specific class
          }
          else if (labelType === 'Sources') {
            let sourceName = ["Blog", "Forum", "Review"].includes(name) ? name.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'social-media';
            div.textContent = `Sources: ${name}`; // Display "Sentiment: Positive"
            div.className = `source-label source-${sourceName.toLowerCase()}`; // Apply sentiment-specific class
          }
          // Handle other label types (non-Gender, non-Age, non-Sentiment)
          else {
            // For labels with sentiment enabled, show name and state, e.g., "LabelName (Positive)"
            if (labelTypes[labelType]?.sentimentEnabled) {
              div.textContent = `${name} (${state})`;
              const stateClass = state ? `${state.toLowerCase().replace(/\s+/g, '-')}` : ''; // Create class based on sentiment state
              div.className = `label-tag ${stateClass}`; // Apply class based on sentiment state (e.g., 'positive', 'negative')
            } else {
              div.textContent = name; // For non-sentiment labels, just display the label name
              div.className = 'other-label-tag active'; // Apply a generic active label class
            }
          }

          list.appendChild(div); // Add the newly created div to the selected labels list
        } else {
          console.warn(`Skipping invalid label object:`, labelObj); // Log a warning for any invalid label objects
        }
      });
    });
  };

  // Function to update the button's visual state (e.g., 'positive', 'negative', etc.)
  const updateLabelButtonState = (button, state) => {
    const states = ['positive', 'negative', 'mixed', 'active', 'inactive'];  // Define all possible states
    states.forEach(s => button.classList.remove(s));  // Remove any existing state class from the button
    button.classList.add(state);  // Add the new state class to the button
  };

  // Function to update the labels displayed on the page based on the active tab and suggested labels
  const updateLabelingPageLabels = () => {
    const container = document.getElementById('label-labels');  // Get the container for displaying labels
    // If the container is not found, exit early
    if (!container) return;
    container.innerHTML = '';  // Clear any existing content
    // Ensure there is an active tab before proceeding
    if (!activeTab) return;  // Exit early if no active tab is set
    // Ensure there are active labels to work with
    if (!activeLabels) return;

    const relevantLabels = new Set();  // Use a set to store relevant labels without duplicates

    // Add active labels for the current active tab to relevantLabels
    Object.entries(activeLabels ?? {})
      .filter(([labelType, labelsArray]) => labelType === activeTab && Array.isArray(labelsArray))  // Ensure labelsArray is an array
      .forEach(([_, labelsArray]) => {
        labelsArray.forEach((labelObj) => {
          if (labelObj?.name) {
            relevantLabels.add({ name: labelObj.name, type: activeTab ?? '' });  // Add each active label to relevant labels
          }
        });
      });


    // Add suggested labels to relevantLabels if they match the active tab
    suggestedLabels
      .filter((lbl) => lbl.type === activeTab)  // Filter suggested labels to match the current active tab
      .forEach((lbl) => {
        relevantLabels.add(lbl);  // Add each suggested label to the relevant labels set
      });

    // Display the relevant labels under the current active tab
    relevantLabels.forEach(({ name, type }) => {
      if (type === activeTab) {
        // Prevent duplicate buttons for the same label
        const existingButton = Array.from(container.children).find(button => button.textContent === name);
        if (!existingButton) {
          const button = document.createElement('button');  // Create a new button for the label
          button.textContent = name;  // Set the button's text to the label name
          button.className = 'label-button';  // Apply the appropriate CSS class for the label button
          button.setAttribute('data-name', name);  // Add the label's name as a data attribute
          button.setAttribute('data-type', type);  // Add the label's type as a data attribute
          button.onclick = () => handleLabelClick(name, type);  // Add an onclick handler for the button

          // Find the label's state by searching the activeLabels for the current tab
          const labelGroup = activeLabels[activeTab] || [];  // Get the labels array for the active tab
          const labelObj = labelGroup.find(l => l.name === name);  // Find the label object by name
          const state = labelObj?.state || 'inactive';  // Get the label's state, default to 'inactive'

          updateLabelButtonState(button, state);  // Update the button's visual state based on the label's state

          container.appendChild(button);  // Add the newly created button to the container
        }
      } else {
        console.log(`Label not displayed (type mismatch or already exists): ${name}`);  // Log if a label isn't displayed
      }
    });

    setupManualLabelInput();  // Set up the input for manually adding new labels
  };

  // Function to set up manual label input handling for manually adding new labels
  const setupManualLabelInput = () => {
    const input = document.getElementById('manual-label-input');  // Get the manual label input field
    const suggestionsContainer = document.getElementById('label-suggestions');  // Get the container for label suggestions

    // Add an event listener for when the user types in the input field
    input.addEventListener('input', function () {
      const query = input.value.toLowerCase();  // Get the input value and convert it to lowercase
      suggestionsContainer.innerHTML = '';  // Clear any previous suggestions

      if (query.length > 0) {
        // Get the labels for the active tab (i.e., the current label type)
        const labelsForTab = labels[activeTab] || [];

        // Filter the labels based on the query and exclude predefined types like Gender, Age, and Sentiment
        const filteredLabels = labelsForTab
          .filter(labelObj =>
            labelObj.name.toLowerCase().includes(query) &&
            !['Gender', 'Age', 'Sentiment', 'Sources'].includes(labelObj.type)  // Exclude these label types from suggestions
          )
          .map(labelObj => labelObj.name);  // Get only the label names

        // Create suggestion elements for each filtered label
        filteredLabels.forEach((label) => {
          const suggestionDiv = document.createElement('div');  // Create a div for the suggestion
          suggestionDiv.textContent = label;  // Set the suggestion text to the label name
          suggestionDiv.onclick = () => {
            addManualLabel(label);  // Add the selected label when clicked
            input.value = '';  // Clear the input field
            suggestionsContainer.innerHTML = '';  // Clear the suggestions
          };
          suggestionsContainer.appendChild(suggestionDiv);  // Append the suggestion to the suggestions container
        });
      }
    });

    // Add an event listener for when the user presses the "Enter" key
    input.addEventListener('keypress', function (e) {
      if (e.key === 'Enter') {
        const query = input.value.trim();  // Trim whitespace from the input value
        if (query.length > 0) {
          // Get the labels for the active tab (i.e., the current label type)
          const labelsForTab = labels[activeTab] || [];

          // Filter out predefined types like Gender, Age, and Sentiment and get valid label names
          const validLabels = labelsForTab
            .filter(labelObj =>
              !['Gender', 'Age', 'Sentiment', 'Sources'].includes(labelObj.type)  // Exclude these label types
            )
            .map(labelObj => labelObj.name);  // Get only the label names

          // Add the label if it's valid (exists in the label list for the current tab)
          if (validLabels.includes(query)) {
            addManualLabel(query);  // Add the manually input label
            input.value = '';  // Clear the input field
            suggestionsContainer.innerHTML = '';  // Clear the suggestions
          }
        }
      }
    });
  };


  /************************************************
 * EVENT HANDLERS 
 ************************************************/

  // Handle changes to the verbatim input field
  const handleVerbatimChange = (event, index) => {
    const newContent = [...verbatimContent];
    newContent[index] = { ...newContent[index], content: event.target.value };
    setVerbatimContent(newContent);
    
    // Update the suggestions based on the new content
    const allText = newContent
      .filter(item => item.type === 'text')
      .map(item => item.content)
      .join(' ');
    suggestLabelsFromVerbatim(allText);
  };

  // Add a new verbatim input field
  const handlePaste = async (event, index) => {
    event.preventDefault();
    const clipboardData = event.clipboardData;
    const items = clipboardData.items;
    
    // checker si le presse-papiers contient des données
    if (!items) return;
    
    let hasHandledItem = false;
    
    for (let i = 0; i < items.length; i++) {
      // Check if the item is an image
      if (items[i].type.indexOf('image') !== -1) {
        hasHandledItem = true;
        updateStatusMessage('Processing pasted image...');
        
        try {
          const file = items[i].getAsFile();
          if (file) {
            const base64Image = await imageToBase64(file);

            setVerbatimContent(prev => {
              if (typeof prev === 'string') {
                return [{ type: 'text', content: prev }, { type: 'image', content: base64Image }];
              } else if (Array.isArray(prev)) {
                const existingImageIndex = prev.findIndex(item => item.type === 'image'); 

                if (existingImageIndex !== -1) {
                  const newContent = [...prev];
                  newContent[existingImageIndex] = { type: 'image', content: base64Image };

                  return newContent;
                } else {
                  return [...prev, { type: 'image', content: base64Image }];
                }
              }
            });
            updateStatusMessage('Image pasted successfully!');
          }
        } catch (error) {
          console.error('Error while processing the pasted image', error);
          updateStatusMessage('Error while processing the image. Please try again.', 'error');
        }
        break;
      }
    }
    
    // if no image was handled, check for text
    if (!hasHandledItem) {
      const text = clipboardData.getData('text');
      if (text) {
        // Update textarea content with pasted text
        const newContent = [...verbatimContent];
        const startPos = event.target.selectionStart;
        const endPos = event.target.selectionEnd;
        const currentContent = newContent[index].content;
        
        // Insert the pasted text at the cursor position
        newContent[index] = { 
          ...newContent[index], 
          content: currentContent.substring(0, startPos) + text + currentContent.substring(endPos) 
        };
        
        setVerbatimContent(newContent);
        
        // Update the suggestions based on the new content
        const allText = newContent
          .filter(item => item.type === 'text')
          .map(item => item.content)
          .join(' ');
        suggestLabelsFromVerbatim(allText);
      }
    }
  };

  // Handle changes to the link input field
  const handleLinkChange = (event) => {
      const url = event.target.value;
      setLink(url);  // Update state with the new link value
      setTranscriptError("");
      const result = checkSourceFromUrl(url);
      if (result && result.checked === true) setShowTranscriptButton(true);
      else setShowTranscriptButton(false);
  };

  const updateSourceFromLink = () => {
    setTranscriptError('');
    if (link !== '') {
      try {
        const { hostname, hostnameWithoutTld, baseName, baseNameFormatted } = extractDomainInfo(link?.trim());
        
        if (hostname && hostnameWithoutTld && baseName && baseNameFormatted) {
          let sourceName = "";
          // Check the hostname against the source categories to determine the source
          labels["Sources"]?.forEach((source) => {
            if (source.keywords.includes(hostnameWithoutTld)) {
              sourceName = source.name;  // Set the source name if a keyword matches
            }
          })

          if (sourceName === "") sourceName = baseNameFormatted; // Default to the base name if no keyword matches
          // Set the active labels with the source information
          if (!sourceOptions.includes(sourceName) && !["Blog", "Forum", "Review"].includes(sourceName)) setSourceOptions(prev => [...prev, sourceName]); // Add the source to the options if not already present
          setActiveLabels((prevLabels = {}) => {
            return {
              ...prevLabels,
              Sources: [
                { name: sourceName, state: 'active', type: 'Sources' }
              ]
            };
          });
        } else {
          setSourceOptions(labels['Sources']?.map(source => source.name) || ["Blog", "Forum", "Review"]); // Reset to default options if no link is provided
          // Delete the "Sources" only if the link is not valid
          setActiveLabels((prevLabels = {}) => {
            const { Sources, ...rest } = prevLabels;
            return rest;
          });
          setTranscriptError('Please provide a valid link, for example: https://www.tiktok.com...');
        }
      } catch (error) {
        console.error("Erreur lors de l'extraction du nom de domaine :", error);
      }
    } else {
      setSourceOptions(labels['Sources']?.map(source => source.name) || ["Blog", "Forum", "Review"]); // Reset to default options if no link is provided
      // Delete the "Sources" only if the link is not valid
      setActiveLabels((prevLabels = {}) => {
        const { Sources, ...rest } = prevLabels;
        return rest;
      });
      setTranscriptError('Please provide a valid link, for example: https://www.tiktok.com...');
    }
  };

      
  // function to fetch Transcript data from TikTok or YouTube
  const fetchTranscriptData = async () => {
    const result = checkSourceFromUrl(link);
    if (!result || result.checked === false || !['tiktok', 'youtube'].includes(result.source)) return;

    try {
      let url = null
      setLoadTranscriptFromURlLoading(true);
      setTranscriptError("");
      // create the URL for fetching the transcript based on the source
      if (result.source === 'tiktok') {
        // Extract the video ID from the URL using regex
        const match = link.match(/\/video\/(\d+)/);
        let videoId = match && match[1] ? match[1] : null 
        url = `https://proxysubtitles.dynvibe.com/tiktok-subtitles/${videoId}`;
      } else if (result.source === 'youtube') {
        url = `https://proxysubtitles.dynvibe.com/youtube-subtitles?url=${link}&use_proxy=false`;
      }
      // const test = await axios.get('https://trusting-lamport.46-21-195-31.plesk.page/cors-test.php')
      // console.log(test)
      updateStatusMessage('Processing transcript...');
      const response = await axios.get(url, {
        headers: {
          'Accept': 'application/json',
          'Content-Type': 'application/json',
        }
      });
      let transcript = null;
      
      if (result.source === 'tiktok') {
        if (response?.data?.eng_US)  transcript = response.data.eng_US.subtitles;
        else if (response?.data?.first_non_eng) transcript = response.data.first_non_eng.subtitles;
      } else if (result.source === 'youtube') {
        if (response?.data?.transcript) transcript = response.data.transcript;
      }

      if (transcript) setVerbatimContent(prev => {
        if (typeof prev === 'string') {
          return [{ type: 'text', content: transcript }];
        } else if (Array.isArray(prev)) {
          const existingTextIndex = prev.findIndex(item => item.type === 'text');

          if (existingTextIndex !== -1) {
            const newContent = [...prev];
            newContent[existingTextIndex] = { type: 'text', content: transcript }; // Update the existing image with the new subtitles
            return newContent;
          } else {
            return [{ type: 'text', content: transcript }, ...prev]; // Add the new subtitles as a text item
          }
        }
      });
      updateStatusMessage('Transcript fetched successfully!');
    } catch (error) {
      setTranscriptError('No transcript is available for this video. If you believe this is an error, please contact the administrator.');
    } finally {
      setLoadTranscriptFromURlLoading(false);
    }
  };

  // Handle click on a gender button
  const handleGenderClick = (gender) => {
    setActiveLabels((prevLabels = {}) => {
      const currentGenderLabels = Array.isArray(prevLabels['Gender']) ? prevLabels['Gender'] : [];

      const isSelected = currentGenderLabels.some(label => label?.name === gender);

      const updatedGenderLabels = isSelected
        ? [] // Deselect if already selected
        : [{ name: gender, state: 'active', type: 'Gender' }];

      return { ...prevLabels, Gender: updatedGenderLabels };
    });
  };

  // Handle click on an age button
  const handleAgeClick = (age) => {
    setActiveLabels((prevLabels = {}) => {
      const currentAgeLabels = Array.isArray(prevLabels['Age']) ? prevLabels['Age'] : [];

      // Check if the age is already selected
      const isSelected = currentAgeLabels.some(label => label?.name === age);

      // If selected, remove it; otherwise, add it as the only active age (single selection for Age)
      const updatedAgeLabels = isSelected
        ? [] // Deselect all ages if clicking an already selected one
        : [{ name: age, state: 'active', type: 'Age' }];

      return { ...prevLabels, Age: updatedAgeLabels };
    });
  };

  // Handle click on an source button
  const handleSourceClick = (source) => {
    setActiveLabels((prevLabels = {}) => {
      const currentSourceLabels = Array.isArray(prevLabels['Sources']) ? prevLabels['Sources'] : [];

      // Check if the age is already selected
      const isSelected = currentSourceLabels.some(label => label?.name === source);

      // If selected, remove it; otherwise, add it as the only active source (single selection for Age)
      const updatedSourceLabels = isSelected
        ? [] // Deselect all sources if clicking an already selected one
        : [{ name: source, state: 'active', type: 'Sources' }];

      return { ...prevLabels, Sources: updatedSourceLabels };
    });
  };

  // Handle click on a sentiment button
  const handleSentimentClick = (sentiment) => {
    setActiveLabels((prevLabels = {}) => {
      const currentSentimentLabels = Array.isArray(prevLabels['Sentiment']) ? prevLabels['Sentiment'] : [];

      const isSelected = currentSentimentLabels.some(label => label?.name === sentiment);

      const updatedSentimentLabels = isSelected
        ? [] // Deselect if already selected
        : [{ name: sentiment, state: sentiment, type: 'Sentiment' }];

      return { ...prevLabels, Sentiment: updatedSentimentLabels };
    });
  };

  // Function to handle clicks on non-special labels (i.e., not Gender, Age, or Sentiment)
  const handleLabelClick = (name, type) => {
    // Skip handling for Gender, Age, and Sentiment labels as they have their own logic
    if (['Gender', 'Age', 'Sentiment', 'Sources'].includes(type)) {
      return;  // Exit early if one of the special labels is clicked
    }

    // Check if the clicked label type supports sentiment (using labelTypes)
    if (labelTypes[type]?.sentimentEnabled) {
      cycleLabelState(name, true, type);  // Cycle sentiment states if sentiment is enabled for this label type
    } else {
      // For non-sentiment labels, toggle between active and inactive states
      setActiveLabels((prevLabels) => {
        const labelGroup = prevLabels[type] || [];  // Get existing labels for this type or initialize as empty
        const labelExists = labelGroup.find((l) => l.name === name);  // Check if the clicked label is already active

        if (labelExists) {
          // If the label is already active, remove it from the group
          const updatedLabelGroup = labelGroup.filter((l) => l.name !== name);
          return updatedLabelGroup.length
            ? { ...prevLabels, [type]: updatedLabelGroup }  // Update state with the remaining labels
            : { ...prevLabels, [type]: undefined };  // If no labels remain, remove the type from active labels
        } else {
          // Check if multiple values are allowed for this label type
          const isMultiValueAllowed = labelTypes[type]?.multiValue !== false;
          const newLabel = { name, state: 'active', type };  // Create a new label object with state 'active'
          // If multiple values are not allowed and there's already an active label, replace it else add the new label
          if (!isMultiValueAllowed && labelGroup.length > 0) {
            return { ...prevLabels, [type]: [newLabel] }; // Replace existing label with the new one
          } else {
            return { ...prevLabels, [type]: [...labelGroup, newLabel] }; // Add the new label to the group
          }
        }
      });
    }
  };

  // Toggle the lock state to enable/disable modifications
  const handleLockClick = () => {
    setIsLockActive(!isLockActive);  // Toggle the isLockActive state between true and false
  };

  /************************************************
* UTILITY FUNCTIONS
************************************************/

  const sentimentStates = ['inactive', 'positive', 'negative', 'mixed'];  // Define possible sentiment states for cycling

  // Function to cycle through label states, handling sentiment labels if enabled
  const cycleLabelState = (labelName, isSentimentEnabled, labelType) => {
    setActiveLabels((prevLabels) => {
      const labelGroup = prevLabels[labelType] || [];  // Get existing labels for the label type or initialize as empty
      const labelObj = labelGroup.find((l) => l.name === labelName);  // Find the specific label object by name
      const currentState = labelObj?.state || 'inactive';  // Set the current state, default to 'inactive' if not found

      let updatedLabelGroup;  // Declare variable to hold the updated label group
      let newState = "inactive";  // Initialize newState as 'inactive'

      if (isSentimentEnabled) {
        // If sentiment is enabled, cycle through the predefined sentiment states
        const currentIndex = sentimentStates.indexOf(currentState);  // Find the current state's index
        const nextIndex = (currentIndex + 1) % sentimentStates.length;  // Get the next state in the cycle
        newState = sentimentStates[nextIndex];  // Set newState to the next sentiment state

        if (newState === "inactive") {
          // If the new state is 'inactive', remove the label from the active group
          updatedLabelGroup = labelGroup.filter((l) => l.name !== labelName);
        } else {
          // Check if multiple values are allowed for this label type
          const isMultiValueAllowed = labelTypes[labelType]?.multiValue !== false;
          // If the label should remain active, update its state or add it if it's new
          if (labelObj) {
            updatedLabelGroup = labelGroup.map((l) =>
              l.name === labelName ? { ...l, state: newState } : l  // Update state for the existing label
            );
          } else {
            // The label does not exist yet
            const newLabel = { name: labelName, state: newState, type: labelType };
            // If multiple values are not allowed and there's already an active label, replace it else add the new label
            if (!isMultiValueAllowed && labelGroup.length > 0) {
              updatedLabelGroup = [newLabel];
            } else {
              updatedLabelGroup = [...labelGroup, newLabel];
            }
          }
        }
      }

      // Immediately update the styling of the button representing this label
      const button = document.querySelector(`button[data-name="${labelName}"][data-type="${labelType}"]`);
      if (button) {
        updateLabelButtonState(button, newState);  // Call function to update the button's visual state
      }

      // Return the updated state with the modified label group for the given label type
      return {
        ...prevLabels,
        [labelType]: updatedLabelGroup,
      };
    });
  };

  // Function to handle the addition of manual labels
  const addManualLabel = (label) => {

    // Prevent adding predefined label types manually (e.g., Gender, Age, Sentiment)
    if (['Gender', 'Age', 'Sentiment', 'Sources'].includes(activeTab)) {
      alert('You cannot manually add Gender, Age, or Sentiment labels.');  // Show an alert if user tries to add these
      return;  // Exit the function if these label types are attempted
    }

    // Get the labels for the active tab (current label type)
    const currentLabelType = labels[activeTab];

    if (currentLabelType) {
      // Check if the label is already active or does not exist in the current label type
      if (!activeLabels[activeTab]?.find(l => l.name === label) && currentLabelType.find(lbl => lbl.name === label)) {
        let newState;
        // If the label type has sentiment enabled, default the state to 'positive'
        if (labelTypes[activeTab]?.sentimentEnabled) {
          newState = 'positive';  // Default sentiment-enabled labels to 'positive'
        } else {
          newState = 'active';  // For other labels, set the state to 'active'
        }

        const newLabel = { name: label, state: newState };  // Create a new label object with the default state
        // Check if multiple values are allowed for this label type
        const isMultiValueAllowed = labelTypes[activeTab]?.multiValue !== false;
        // Update the activeLabels state to include the new label
        setActiveLabels((prevLabels) => {
          const labelGroup = prevLabels[activeTab] || [];
          // if multiple values are not allowed and there's already an active label, replace it else if add the new label else create new array
          if (!isMultiValueAllowed && labelGroup.length > 0) {
            return {
              ...prevLabels,
              [activeTab]: [newLabel] // Replace existing label with the new one
            };
          } else if (prevLabels[activeTab]) {
            return {
              ...prevLabels,
              [activeTab]: [...prevLabels[activeTab], newLabel],  // Append the new label to the current label array
            };
          } else {
            return { ...prevLabels, [activeTab]: [newLabel] };  // Create a new array if none exists for the label type
          }
        });

        // Re-run the function to update the displayed labels in the "Additional Labels" section
        updateLabelingPageLabels();  // Ensure the new label is displayed
      } else if (activeLabels[activeTab]?.find(l => l.name === label)) {
        // If the label is already active, show an alert
        alert('Label is already active.');
      } else {
        // If the label doesn't exist in the list, show an alert
        alert('Label does not exist in the label list.');
      }
    } else {
      // If no labels are found for the current tab, show an alert
      alert('No labels found for the current label type.');
    }
  };

  const handleQuickAddLabel = async (label, tab) => {
    const success = await addLabel(label, tab); // Assume addLabel returns a success boolean or promise
  
    if (success) {
      // Reset the input field
      const inputElement = document.getElementById('quick-label-add');
      if (inputElement) {
        inputElement.value = ''; // Clear the input field
      }
  
      // Set a success status message
      updateStatusMessage(`The label "${label}" was successfully added with type "${tab}".`);
  
      addManualLabel(label); // Execute only if successful
    } else {
      // Handle failure case (optional)
      updateStatusMessage(`Failed to add the label "${label}". Please try again.`, 'error');
    }
  };
  

  // Function to suggest labels based on the current verbatim text
  const suggestLabelsFromVerbatim = (text) => {
    const foundLabels = [];  // Array to store any labels found in the text

    // Iterate over each label type (e.g., Gender, Age, Sentiment)
    Object.keys(labels).forEach((labelType) => {
      const labelArray = labels[labelType];  // Get the array of labels for this label type

      // Safeguard: Ensure labelArray is an array before iterating
      if (Array.isArray(labelArray)) {
        // Loop through each label in the label type
        labelArray.forEach((labelObj) => {
          // Check if the label has associated keywords, then match those against the verbatim text
          if (Array.isArray(labelObj.keywords) && labelObj.keywords.length > 0) {
            labelObj.keywords.forEach(keywordString => {
              const individualKeywords = keywordString.split(/[;,]/).map(kw => kw.trim());  // Split by ';' or ',' and trim spaces
              // Check if any individual keyword matches the verbatim text
              if (
                Array.isArray(individualKeywords) &&
                individualKeywords.some(keyword => text?.toLowerCase().includes(keyword?.toLowerCase()))
              ) {
                if (labelObj?.name && labelType) {
                  foundLabels.push({ name: labelObj.name, type: labelType });  // Add matching labels to foundLabels
                }
              }
            });
          // If attribute label has no keywords, add it to the found labels
          } else if (Array.isArray(labelObj.keywords) && labelObj.keywords.length === 0) {
            if (labelObj?.name && labelType) {
              foundLabels.push({ name: labelObj.name, type: labelType });
            }
          }
        });
      } else {
        console.warn(`Expected array but got:`, labelArray);  // Warn if labelArray is not an array
      }
    });

    // Set the found labels as suggested labels for the relevant tabs
    setSuggestedLabels(foundLabels);

    // Update the labels displayed in the "Additional Labels" area
    updateLabelingPageLabels();
  };

  const extractPlainTextFromVerbatimContent = (content) => {
    if (!Array.isArray(content)) {
      return '';
    }

    return content
      .filter(item => item?.type === 'text')
      .map(item => item?.content || '')
      .join('\n')
      .trim();
  };

  const extractTranslationFromServiceResponse = (responseData) => {
    const payload = Array.isArray(responseData) ? responseData[0] : responseData;

    if (!payload) {
      return '';
    }

    if (typeof payload === 'string') {
      return payload.trim();
    }

    return (
      payload.translation ||
      payload.translatedText ||
      payload.text ||
      payload.content ||
      payload?.translations?.[0]?.text ||
      payload?.translations?.[0]?.translatedText ||
      payload?.translations?.[0]?.translation ||
      payload?.data?.translations?.[0]?.text ||
      ''
    ).trim();
  };

  const storeVerbatim = async () => {
    // Ensure labels are always stored as arrays
    const ensureLabelsAreArrays = (labels) => {
      const result = {};
      for (const labelType in labels) {
        if (labels[labelType] !== undefined && labels[labelType] !== null) {
            result[labelType] = Array.isArray(labels[labelType]) ? labels[labelType] : [labels[labelType]];
        }
      }
      return result;
    };

    // Check for mandatory labels before saving
    const checkMandatoryLabels = () => {
      for (const labelType in labelTypes) {
        if (labelTypes[labelType]?.mandatory) {
          const labelsArray = activeLabels[labelType];
          if (!labelsArray || labelsArray.length === 0) {
            alert(`Please select at least one label for the mandatory label type: ${labelType}`);
            return false;
          }
        }
      }
      return true;
    };

    // Check for multi-value constraints on labels
    const checkMultiValueLabels = () => {
      for (const labelType in labelTypes) {
        if (labelTypes[labelType]?.multiValue === false) {
          const labelsArray = activeLabels[labelType];
          if (Array.isArray(labelsArray) && labelsArray.length > 1) {
            alert(`You can only select one label for the label type: ${labelType}`);
            return false;
          }
        }
      }
      return true;
    };

    // Main logic to store the verbatim
    // Vérifier si le verbatim a au moins un contenu
    const hasContent = verbatimContent.some(item => 
      (item.type === 'text' && item.content.trim() !== '') || 
      (item.type === 'image' && item.content)
    );

    let translationFailed = false;
    let translationErrorMessage = '';

    if (hasContent && link && checkMandatoryLabels() && checkMultiValueLabels()) {
      const newID = editingVerbatimId ?? generateNextID();
      let translation = editingVerbatimId !== null ? editingVerbatimTranslation : undefined;

      if (autoTranslationOnSave) {
        const plainText = extractPlainTextFromVerbatimContent(verbatimContent);

        if (plainText) {
          try {
            const response = await axios.post(
              TRANSLATION_ENDPOINT,
              {
                texts: [{ Text: plainText }],
                language: 'en',
              },
              {
                headers: {
                  'Content-Type': 'application/json',
                },
              }
            );

            translation = extractTranslationFromServiceResponse(response?.data) || '';
          } catch (error) {
            console.error('Error while translating verbatim on save:', error);
            translationFailed = true;
            translationErrorMessage =
              error?.response?.data?.error?.message ||
              error?.response?.data?.message ||
              error?.message ||
              'Unknown translation error';
            translation = editingVerbatimId !== null ? editingVerbatimTranslation : '';
          }
        } else {
          translationFailed = true;
          translationErrorMessage = 'No translatable text was found in the verbatim.';
          translation = editingVerbatimId !== null ? editingVerbatimTranslation : '';
        }
      }

      const newVerbatim = {
        id: newID,
        verbatim: verbatimContent,
        translation,
        link,
        labels: ensureLabelsAreArrays(activeLabels),
        autoTranslationOnSave,
      };
      // Add the source to the Sources label if it is not already present
      const { hostname, hostnameWithoutTld, baseName, baseNameFormatted } = extractDomainInfo(link?.trim());

      if (hostname && hostnameWithoutTld && baseName && baseNameFormatted ) {
        const sourceLabels = Array.isArray(newVerbatim?.labels?.Sources) ? newVerbatim.labels.Sources : [];

        if (["Blog", "Forum", "Review"].includes(sourceLabels[0]?.name)) {
          setLabels((prevLabels) => {
            const updatedLabels = { ...prevLabels };
            // Ensure 'Source' label exists
            if (!updatedLabels['Sources']) {
              updatedLabels['Sources'] = [];
            }
            updatedLabels['Sources'].forEach((source) => {
              if (source.name === sourceLabels[0].name && !source.keywords.includes(hostnameWithoutTld)) {
                source.keywords.push(hostnameWithoutTld);
              }
              return source;
            });

            return updatedLabels;
          })
        } else if (labels['Sources'] && !labels['Sources'].some(source => source.name?.toLowerCase() === baseName)) {
          // If the source is not already in the Sources label, add it
          setLabels((prevLabels) => ({
            ...prevLabels,
            Sources: [...(prevLabels['Sources'] || []), { name: baseNameFormatted, keywords: [hostnameWithoutTld], explanation: '' }],
          }));
        }
      }

      setSourceOptions(labels['Sources']?.map(source => source.name) || []); // Update source options

      if (editingVerbatimId !== null) {
        // If editing, update the verbatim by replacing it
        editVerbatim(newID, newVerbatim);
      } else {
        // If adding new, use the addVerbatim function
        addVerbatim(newVerbatim);
        setLastID(newID); // Update lastID to the latest ID used
      }

      // Reset form fields and state after storing the verbatim
      setVerbatimContent([{ type: 'text', content: '' }]);
      if (isLockActive) {
        const lockedLabels = Object.fromEntries(
          Object.entries(activeLabels || {}).filter(([key]) => key === 'Sources')
        );
        setActiveLabels(lockedLabels); // Clear Sources if locked
        setSourceOptions(prev =>
          prev.includes(lockedLabels['Sources'][0]?.name) ? prev : [...prev, lockedLabels['Sources'][0]?.name]
        );
      } else {
        setLink('');
        setActiveLabels({});
      }
      setSuggestedLabels([]);
      setEditingVerbatimId(null);
      setEditingVerbatimTranslation('');

      // Set a success or warning status message
      if (translationFailed) {
        updateStatusMessage(
          `Verbatim stored, but translation failed: ${translationErrorMessage}`,
          'error'
        );
      } else {
        updateStatusMessage('Verbatim successfully stored');
      }
    } else {
      alert('Please fill in all required fields.');
    }
  };

  // Add a function to handle image uploads
  const handleImageUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    try {
      updateStatusMessage('Image optimization in progress...');
      // Use imageToBase64 to optimize and compress the image
      // this function will resize and compress the image before converting it to Base64
      const base64Image = await imageToBase64(file);
      
      // Add the image to the verbatim content
      setVerbatimContent(prev => {
        if (typeof prev === 'string') {
          return [{ type: 'text', content: prev }, { type: 'image', content: base64Image }];
        } else if (Array.isArray(prev)) {
          const existingImageIndex = prev.findIndex(item => item.type === 'image');          
          // if an image already exists, replace it
          if (existingImageIndex !== -1) {
            // create a new array to avoid mutating the state directly
            const newContent = [...prev];
            // replace the existing image with the new one
            newContent[existingImageIndex] = { type: 'image', content: base64Image };
            return newContent;
          } else {
            // else, add the new image to the end of the array
            return [...prev, { type: 'image', content: base64Image }];
          }
        }
      });
      updateStatusMessage('Image pasted successfully!');
    } catch (error) {
      console.error('Error while processing the pasted image', error);
      updateStatusMessage('Error while processing the image. Please try again.', 'error');
    }
  };

  // Fuction to delete an image from the verbatim content
  const removeImage = (index) => {
    const newContent = [...verbatimContent];
    newContent.splice(index, 1);
    setVerbatimContent(newContent);
  };

  // Handle deleting a specific verbatim by ID
  const handleDeleteVerbatim = (id) => {
    if (window.confirm("Are you sure you want to delete this verbatim?")) {
      // Ask for confirmation before deleting
      deleteVerbatim(id); // Call the delete function passed as a prop
      setVerbatimContent([{ type: 'text', content: '' }]);
      setLink('');
      setActiveLabels({});
      setEditingVerbatimId(null);
      setEditingVerbatimTranslation('');
      updateStatusMessage("Verbatim successfully deleted");
    }
  };

  return (
    <div className="labeling-container">
      <div className="review-input">
        <h2>Verbatim*</h2>
        
        {/* Section de contenu du verbatim */}
        <div className="verbatim-content-container-labeling-page">
          {verbatimContent.map((item, index) => (
            <div key={index} className="verbatim-content-block">
              {item.type === 'text' ? (
                <textarea
                  className="verbatim-text-area"
                  placeholder="Type your text here, or paste text or an image..."
                  value={item.content}
                  onChange={(e) => handleVerbatimChange(e, index)}
                  onPaste={(e) => handlePaste(e, index)}
                  autoCorrect="off"
                  spellCheck="false"
                  style={{ height: verbatimContent.some(content => content.type === 'image') ? '150px' : '300px' }}
                />
              ) : (
                <div className="verbatim-image-container">
                  <img 
                    src={base64ToImage(item.content)} 
                    alt="Verbatim image" 
                    className="verbatim-image"
                    onError={(e) => {
                      console.error("Image loading error", e);
                      e.target.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iI2YwZjBmMCIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMjAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGZpbGw9IiM5OTkiPkltYWdlIEVycm9yPC90ZXh0Pjwvc3ZnPg==";
                    }}
                  />
                  <span 
                    className="remove-image-icon"
                    onClick={() => removeImage(index)}
                  >
                    <i className="fa-solid fa-times"></i>
                  </span>
                </div>
              )}
            </div>
          ))}
          
          <div className="verbatim-controls">
            <button className="styled-button" style={{ fontSize: "0.9em", padding: "7px" }} onClick={() => imageInputRef.current.click()}>
              <i className="fa-solid fa-image"></i> Add an image
            </button>
            
            <input
              type="file"
              ref={imageInputRef}
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageUpload}
            />
          </div>
        </div>
        
        <div className="link-input">
          <h4>
            <label htmlFor="verbatim-link">Verbatim Link*:</label> 
            {link && <a href={link} target="_blank" rel="noopener noreferrer"><i className="fa-solid fa-link"></i></a>}
          </h4>
          <div className="input-with-icon">
            <input
              type="text"
              id="verbatim-link"
              placeholder="Enter the link to the verbatim"
              value={link}
              onChange={handleLinkChange}
              onBlur={updateSourceFromLink}
            />
            <i
              className={`fa-solid ${isLockActive ? 'fa-lock' : 'fa-lock-open'} lock-icon`}
              onClick={handleLockClick}
            ></i>
          </div>
          {transcriptError && <small className="transcript-error-message">{transcriptError}</small>}
          {showTranscriptButton && (
              <button 
                  className="styled-button"
                  onClick={fetchTranscriptData}
                  style={{ marginTop: '10px', fontSize: '0.9em', padding: '5px', cursor: loadTranscriptFromURlLoading ? 'wait' : 'pointer'}}
              >
                {loadTranscriptFromURlLoading ? <Spinner /> : 'Load transcript'}
              </button>
            )}
        </div>
        <div>
          <h5>*Mandatory: a value must be entered.</h5>
        </div>
      </div>
        <div className="label-selection">
          <div className="flex-container label-area-header">
            <h2>Select relevant labels below</h2>
            <div className="labeling-save-actions">
              <label
                className={`labeling-auto-translation-toggle ${autoTranslationOnSave ? 'active' : ''}`}
                role="switch"
                aria-checked={autoTranslationOnSave}
                tabIndex={0}
                onClick={() => setAutoTranslationOnSave(prev => !prev)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setAutoTranslationOnSave(prev => !prev);
                  }
                }}
              >
                <span className="labeling-auto-translation-track" aria-hidden="true">
                  <span className="labeling-auto-translation-thumb" />
                </span>
                <span className="labeling-auto-translation-text">Auto-translation on save</span>
              </label>
              <button
                className="styled-button"
                onClick={() => {
                  storeVerbatim();
                }}
              >
                <i className="fa-solid fa-floppy-disk"></i> Store Verbatims
              </button>
              {editingVerbatimId && 
                <button className="styled-button labeling-delete-button" onClick={() => handleDeleteVerbatim(editingVerbatimId)}>
                  <i className="fa fa-trash"></i>
                </button>
              }
            </div>
          </div>
        <div className="demographics-row">
          <div className="gender-column">
            <h4>Gender*</h4>
            <div id="gender-labels">
              {genderOptions.map((gender) => (
                <button
                  key={gender}
                  className={`label-button gender-button ${Array.isArray(activeLabels?.['Gender']) && activeLabels['Gender'].some(label => label?.name === gender)
                    ? `selected gender-${gender?.toLowerCase().replace(/\s+/g, '-')}`
                    : ''
                    }`}
                  onClick={() => handleGenderClick(gender)}
                >
                  {gender}
                </button>

              ))}

            </div>
          </div>
          <div className="age-column">
            <h4>Age*</h4>
            <div id="age-labels">
              {ageOptions.map((age) => (
                <button
                  key={age}
                  className={`label-button age-button ${Array.isArray(activeLabels?.['Age']) && activeLabels['Age'].some(label => label?.name === age)
                    ? `selected age-${age?.toLowerCase().replace(/[^a-z0-9]/g, '-')}`
                    : ''
                    }`}
                  onClick={() => handleAgeClick(age)}
                >
                  {age}
                </button>

              ))}

            </div>
          </div>
        </div>
        <div className="demographics-row">
          <div className="label-section gender-column">
            <h4>Sentiment*</h4>
            <div id="sentiment-labels">
              {sentiments.map((sentiment) => (
                <button
                  key={sentiment}
                  className={`label-button sentiment-button ${Array.isArray(activeLabels?.['Sentiment']) && activeLabels['Sentiment'].some(label => label?.name === sentiment)
                    ? `selected ${sentiment?.toLowerCase()}`
                    : ''
                    }`}
                  onClick={() => handleSentimentClick(sentiment)}
                >
                  {sentiment}
                </button>

              ))}
            </div>
          </div>
          <div className="age-column">
            <h4>Sources*</h4>
            <div id="source-labels">
              {sourceOptions.map((source) => {
                let sourceName = ["Blog", "Forum", "Review"].includes(source) ? source.toLowerCase().replace(/[^a-z0-9]/g, '-') : 'social-media';
                const isSelected =
                  Array.isArray(activeLabels?.["Sources"]) &&
                  activeLabels["Sources"].some((label) => label?.name === source);

                return <button
                  key={source}
                  ref ={isSelected ? selectedRef : null}
                  className={`label-button source-button ${isSelected ? `selected source-${sourceName}` : ''}`}
                  onClick={() => handleSourceClick(source)}
                >
                  {source}
                </button>
            })}
            </div>
          </div>
        </div>
        <div className="label-section">
          <h4>Additional Labels</h4>
          <div id="tabs-container-wrapper">
            <div id="label-tabs" className="tabs-container">
              {Object.keys(labels)
                .filter(type => !['Gender', 'Age', 'Sentiment', 'Sources'].includes(type)) // Exclude specific label types
                .map((tab) => {
                  const isMandatory = labelTypes[tab]?.mandatory;
                  const isSentimentEnabled = labelTypes[tab]?.sentimentEnabled;
                  const isMultiValue = labelTypes[tab]?.multiValue === false; // Check if multiValues is false

                  return (
                    <button
                      key={tab}
                      className={`label-tab ${activeTab === tab ? 'active' : ''}`}
                      onClick={() => setActiveTab(tab)}
                    >
                      {tab}
                      {isMandatory && <i className="fa-solid fa-exclamation-triangle tab-icon" title="Mandatory"></i>}
                      {isSentimentEnabled && <i className="fa-solid fa-smile tab-icon" title="Sentiment Enabled"></i>}
                      {isMultiValue && <i className="fa-solid fa-tag tab-icon" title="Single Value Only"></i>}
                      {!isMultiValue && <i className="fa-solid fa-tags tab-icon" title="Multiple Values Possible"></i>}
                    </button>
                  );
                })}
            </div>
            <div id="label-labels">
              {activeTab && (
                <div className="labels-list">
                  {labels[activeTab]?.map((labelObj) => {
                    const { name, state } = labelObj;  // Extract label name and state
                    const stateClass = state || 'inactive'; // Default to 'inactive' if state is not set

                    return (
                      <button
                        key={name}
                        className={`label-button ${stateClass}`}  // Apply the correct class dynamically
                        onClick={() => handleLabelClick(name)}
                      >
                        {name}
                        {labelTypes[activeTab]?.sentimentEnabled && (
                          <i className="fa-solid fa-smile sentiment-icon" title="Sentiment Enabled"></i>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <div id="manual-label-add">
              <div id="label-input">
                <input type="text" id="manual-label-input" placeholder="Search a label" />
                <div id="label-suggestions"></div>
              </div>
              <div id="label-quick-add">
                <input
                  type="text"
                  id="quick-label-add"
                  placeholder="Add a label immediately in your setup"
                />
                <MinimalistButton
                  onClick={() => {
                    const inputValue = document.getElementById('quick-label-add').value;
                    handleQuickAddLabel(inputValue, activeTab);
                  }}
                >
                  +
                </MinimalistButton>
              </div>
            </div>

          </div>
        </div>
        <div id="selected-labels">
          <h4>Active Labels:</h4>
          <div id="selected-labels-list" className="flex-container"></div>
          <br />
        </div>
        <div>
          <h5>*Mandatory: a value must be selected.</h5>
        </div>
      </div>
      <StatusBar message={statusMessage} type={statusType} />
    </div>
  );
};

export default LabelingPage;
